'use client';

import { useState } from 'react';
import Link from 'next/link';
import { CheckCircle, ArrowLeft, Loader2, ChevronDown } from 'lucide-react';
import { LandingLayout } from '@/features/landing/LandingLayout';

const PLANS = ['Starter', 'Growth', 'Pro', 'Enterprise', 'Not sure yet'];
const TEAM_SIZES = [
  'Under 10 agents',
  '10–25 agents',
  '26–50 agents',
  '51–100 agents',
  '101–250 agents',
  '250+ agents',
];

export default function RequestAccessPage() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    teamSize: '',
    plan: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/request-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Something went wrong.');
      }

      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Success state ───────────────────────────────────────────────────────────
  if (success) {
    return (
      <LandingLayout>
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#ff5100]" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">Request Received!</h1>
          <p className="text-slate-600 mb-2 leading-relaxed">
            Thanks, <strong>{form.firstName}</strong>! We&apos;ve received your request and will be in touch
            at <strong>{form.email}</strong> within 1 business day.
          </p>
          <p className="text-slate-500 text-sm mb-8">
            Check your inbox for a confirmation email from us.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              href="/"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-100 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <Link
              href="/features"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#ff5100] text-white text-sm font-medium hover:bg-[#e04800] transition-colors"
            >
              Explore Features
            </Link>
          </div>
        </div>
      </div>
      </LandingLayout>
    );
  }

  // ── Form ────────────────────────────────────────────────────────────────────
  return (
    <LandingLayout>
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pb-20">

        {/* Page header */}
        <div className="text-center mb-10">
          <span className="inline-block bg-orange-100 text-[#ff5100] text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-4">
            Get Started
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Request Access to SwishrPulse
          </h1>
          <p className="text-slate-600 text-lg max-w-lg mx-auto">
            Fill in your details below and our team will reach out within{' '}
            <strong>1 business day</strong> to get you set up.
          </p>
        </div>

        {/* Form card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Name row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  First Name <span className="text-[#ff5100]">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                  placeholder="Mahmoud"
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#ff5100]/30 focus:border-[#ff5100] transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Last Name <span className="text-[#ff5100]">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                  placeholder="Al-Rashidi"
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#ff5100]/30 focus:border-[#ff5100] transition-colors"
                />
              </div>
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Work Email <span className="text-[#ff5100]">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="you@company.com"
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#ff5100]/30 focus:border-[#ff5100] transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+971 50 000 0000"
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#ff5100]/30 focus:border-[#ff5100] transition-colors"
                />
              </div>
            </div>

            {/* Company & Job Title */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Company Name <span className="text-[#ff5100]">*</span>
                </label>
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  required
                  placeholder="Acme Corp"
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#ff5100]/30 focus:border-[#ff5100] transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Job Title
                </label>
                <input
                  type="text"
                  name="jobTitle"
                  value={form.jobTitle}
                  onChange={handleChange}
                  placeholder="Head of CX / QA Manager"
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#ff5100]/30 focus:border-[#ff5100] transition-colors"
                />
              </div>
            </div>

            {/* Team size & Plan */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Team Size
                </label>
                <div className="relative">
                  <select
                    name="teamSize"
                    value={form.teamSize}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#ff5100]/30 focus:border-[#ff5100] transition-colors appearance-none bg-white text-slate-900"
                  >
                    <option value="">Select team size…</option>
                    {TEAM_SIZES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Plan Interest
                </label>
                <div className="relative">
                  <select
                    name="plan"
                    value={form.plan}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#ff5100]/30 focus:border-[#ff5100] transition-colors appearance-none bg-white text-slate-900"
                  >
                    <option value="">Select a plan…</option>
                    {PLANS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Anything you&apos;d like us to know?
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={4}
                placeholder="Tell us about your current QA process, pain points, or any specific requirements…"
                className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#ff5100]/30 focus:border-[#ff5100] transition-colors resize-none"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#ff5100] hover:bg-[#e04800] disabled:opacity-60 text-white font-semibold rounded-lg transition-colors text-sm"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending Request…
                </>
              ) : (
                'Submit Access Request →'
              )}
            </button>

            <p className="text-center text-xs text-slate-400">
              By submitting, you agree to our{' '}
              <Link href="/legal/privacy" className="underline hover:text-slate-600">
                Privacy Policy
              </Link>
              . We&apos;ll never share your information.
            </p>
          </form>
        </div>

        {/* Trust row */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
          <span className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-[#ff5100]" />
            No credit card required
          </span>
          <span className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-[#ff5100]" />
            1 business day response
          </span>
          <span className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-[#ff5100]" />
            Managed onboarding included
          </span>
        </div>
      </div>
    </div>
    </LandingLayout>
  );
}
